import { useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { Select } from "@base-ui/react/select";
import { Switch } from "@base-ui/react/switch";
import { Checkbox } from "@base-ui/react/checkbox";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import { Toggle } from "@base-ui/react/toggle";
import { motion, useReducedMotion } from "motion/react";

// Base UI controls bridge the existing form API. Original controls alone own
// names/values; React owns only interaction, focus and popup lifecycles.
const mounted = new Map<
  Element,
  { root: Root; host: HTMLElement; observer: MutationObserver }
>();
let nextId = 0;

function notify(source: HTMLElement, commit = true) {
  source.dispatchEvent(new Event("input", { bubbles: true }));
  if (commit) source.dispatchEvent(new Event("change", { bubbles: true }));
}
function useSource<T>(
  source: HTMLInputElement | HTMLSelectElement,
  read: () => T,
) {
  const [value, setValue] = useState(read);
  useEffect(() => {
    const sync = () => setValue(read());
    const reset = () =>
      queueMicrotask(() => {
        if (source.isConnected) sync();
      });
    source.addEventListener("input", sync);
    source.addEventListener("change", sync);
    source.form?.addEventListener("reset", reset);
    return () => {
      source.removeEventListener("input", sync);
      source.removeEventListener("change", sync);
      source.form?.removeEventListener("reset", reset);
    };
  }, [source]);
  return [value, setValue] as const;
}
function labelFor(source: HTMLInputElement | HTMLSelectElement) {
  const label = source.labels?.[0]?.cloneNode(true) as
    HTMLLabelElement | undefined;
  label
    ?.querySelectorAll("input,select,textarea,button,[data-portal-control]")
    .forEach((node) => node.remove());
  return (
    source.dataset.uiLabel ||
    source.getAttribute("aria-label") ||
    source.labels?.[0]?.querySelector("span")?.textContent?.trim() ||
    label?.textContent?.trim() ||
    source.name ||
    "Выберите значение"
  );
}
function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path d="m7 10 5 5 5-5" />
    </svg>
  );
}
function Tick() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

function Choice({ source }: { source: HTMLSelectElement }) {
  const [value, setValue] = useSource(source, () => source.value);
  const items = Array.from(source.options).map((option) => ({
    value: option.value,
    label: option.textContent || "",
    disabled: option.disabled,
  }));
  const update = (next: string | null) => {
    if (next == null) return;
    source.value = next;
    setValue(next);
    notify(source);
  };
  const label = labelFor(source);
  if (source.dataset.ui === "segments")
    return (
      <ToggleGroup
        className="ui-toggle-group"
        value={[value]}
        onValueChange={(values) => {
          if (values.length) update(String(values[0]));
        }}
        aria-label={label}
        disabled={source.disabled}
      >
        {items.map((item) => (
          <Toggle
            key={item.value}
            value={item.value}
            className="ui-toggle"
            disabled={item.disabled}
          >
            {item.label}
          </Toggle>
        ))}
      </ToggleGroup>
    );
  return (
    <Select.Root
      value={value}
      onValueChange={update}
      items={items}
      disabled={source.disabled}
    >
      <Select.Trigger
        id={source.id + "-ui"}
        className="ui-select-trigger"
        aria-label={label}
        aria-invalid={
          source.getAttribute("aria-invalid") === "true" || undefined
        }
      >
        <Select.Value />
        <Select.Icon>
          <Chevron />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal container={source.closest("dialog") || undefined}>
        <Select.Positioner
          sideOffset={6}
          alignItemWithTrigger={false}
          className="ui-select-positioner"
        >
          <Select.Popup className="ui-select-popup">
            <Select.ScrollUpArrow className="ui-select-scroll">
              ↑
            </Select.ScrollUpArrow>
            <Select.List>
              {items.map((item) => (
                <Select.Item
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  className="ui-select-item"
                >
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Tick />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.List>
            <Select.ScrollDownArrow className="ui-select-scroll">
              ↓
            </Select.ScrollDownArrow>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}
function CheckControl({ source }: { source: HTMLInputElement }) {
  const [checked, setChecked] = useSource(source, () => source.checked);
  const reduced = useReducedMotion();
  const update = (next: boolean) => {
    source.checked = next;
    setChecked(next);
    notify(source);
  };
  const props = {
    checked,
    onCheckedChange: update,
    disabled: source.disabled,
    id: source.id + "-ui",
    "aria-label": labelFor(source),
  };
  const isSwitch = source.matches(
    ".calc-checkbox input, .ai-filter-toggle input, [data-ui='switch']",
  );
  return isSwitch ? (
    <Switch.Root
      {...props}
      render={<button type="button" />}
      className="ui-switch"
    >
      <Switch.Thumb
        render={
          <motion.span
            animate={{ x: checked ? 16 : 0 }}
            transition={
              reduced
                ? { duration: 0 }
                : { type: "spring", stiffness: 500, damping: 35 }
            }
          />
        }
        className="ui-switch-thumb"
      />
    </Switch.Root>
  ) : (
    <Checkbox.Root
      {...props}
      render={<button type="button" />}
      className="ui-checkbox"
    >
      <Checkbox.Indicator>
        <Tick />
      </Checkbox.Indicator>
    </Checkbox.Root>
  );
}

function DateEntry({ source }: { source: HTMLInputElement }) {
  const split = (value: string) => {
    const [year = "", month = "", day = ""] = value.split("-");
    return [day, month, year];
  };
  const [parts, setParts] = useState(() => split(source.value));
  const typing = useRef(false);
  useEffect(() => {
    const sync = () => {
      if (!typing.current) {
        source.setCustomValidity("");
        setParts(split(source.value));
      }
    };
    const reset = () =>
      queueMicrotask(() => {
        if (source.isConnected) sync();
      });
    source.addEventListener("input", sync);
    source.addEventListener("change", sync);
    source.form?.addEventListener("reset", reset);
    return () => {
      source.removeEventListener("input", sync);
      source.removeEventListener("change", sync);
      source.form?.removeEventListener("reset", reset);
    };
  }, [source]);
  const label = labelFor(source);
  const update = (
    index: number,
    raw: string,
    target: HTMLInputElement,
    advance = true,
  ) => {
    const next = [...parts];
    next[index] = raw.replace(/\D/g, "").slice(0, index === 2 ? 4 : 2);
    setParts(next);
    const [day, month, year] = next;
    const complete = day.length > 0 && month.length > 0 && year.length === 4;
    const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    const date = complete ? new Date(iso + "T12:00:00Z") : null;
    const valid =
      date &&
      Number.isFinite(date.getTime()) &&
      date.toISOString().slice(0, 10) === iso &&
      Number(year) >= 1900 &&
      Number(year) <= 2200;
    source.value = valid ? iso : "";
    source.setCustomValidity(
      next.some(Boolean) && !valid ? "Введите дату полностью" : "",
    );
    // Native input listeners keep the source value in sync without replacing a
    // partially typed date or remounting the active segment.
    typing.current = true;
    notify(source, Boolean(valid) || next.every((part) => !part));
    typing.current = false;
    if (advance && next[index].length === (index === 2 ? 4 : 2) && index < 2) {
      const sibling = target
        .closest(".ui-date-entry")
        ?.querySelectorAll<HTMLInputElement>("[data-date-part]")[index + 1];
      sibling?.focus();
      sibling?.select();
    }
  };
  return (
    <span
      className="ui-date-entry"
      data-invalid={
        source.getAttribute("aria-invalid") === "true" ? "" : undefined
      }
    >
      {parts.map((part, index) => (
        <span className="ui-date-piece" key={index}>
          {index > 0 && (
            <span className="ui-date-separator" aria-hidden="true">
              .
            </span>
          )}
          <input
            id={source.id + (index === 0 ? "-ui" : "-part-" + index)}
            data-date-part={index}
            aria-label={`${label}: ${["день", "месяц", "год"][index]}`}
            aria-invalid={source.getAttribute("aria-invalid") === "true" || undefined}
            aria-describedby={source.getAttribute("aria-describedby") || undefined}
            inputMode="numeric"
            type="text"
            autoComplete="off"
            maxLength={index === 2 ? 4 : 2}
            placeholder={["ДД", "ММ", "ГГГГ"][index]}
            value={part}
            disabled={source.disabled}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) =>
              update(index, event.target.value, event.target)
            }
            onKeyDown={(event) => {
              if ([".", "/", "ArrowRight"].includes(event.key) && index < 2) {
                event.preventDefault();
                const next = event.currentTarget
                  .closest(".ui-date-entry")
                  ?.querySelectorAll<HTMLInputElement>("[data-date-part]")[
                  index + 1
                ];
                next?.focus();
                next?.select();
              }
              if (event.key === "Backspace" && !part && index > 0) {
                event.preventDefault();
                const prev = event.currentTarget
                  .closest(".ui-date-entry")
                  ?.querySelectorAll<HTMLInputElement>("[data-date-part]")[
                  index - 1
                ];
                prev?.focus();
                prev?.select();
              }
            }}
            onPaste={(event) => {
              const text = event.clipboardData.getData("text").trim();
              const match =
                /^(\d{2})[./](\d{2})[./](\d{4})$/.exec(text) ||
                /^(\d{4})-(\d{2})-(\d{2})$/
                  .exec(text)
                  ?.map((x, i, a) => (i === 1 ? a[3] : i === 3 ? a[1] : x));
              if (!match) return;
              event.preventDefault();
              const iso = `${match[3]}-${match[2]}-${match[1]}`;
              const parsed = new Date(iso + "T12:00:00Z");
              if (
                Number.isFinite(parsed.getTime()) &&
                parsed.toISOString().slice(0, 10) === iso
              ) {
                source.value = iso;
                source.setCustomValidity("");
                notify(source);
                setParts(split(iso));
              }
            }}
          />
        </span>
      ))}
      <button
        type="button"
        className="ui-date-icon"
        aria-label={`Открыть календарь: ${label}`}
        disabled={source.disabled}
        onClick={() => {
          try {
            source.showPicker();
          } catch {
            source.focus();
          }
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M16 3v4M8 3v4M3 11h18" />
        </svg>
      </button>
    </span>
  );
}

export function focusControl(source: HTMLElement) {
  const apply = (remaining: number) => {
    if (!source.isConnected) return;
    const target = mounted
      .get(source)
      ?.host.querySelector<HTMLElement>("button,[data-date-part]");
    if (
      !target &&
      remaining > 0 &&
      source.matches('select,input[type="checkbox"],input[type="date"]')
    ) {
      requestAnimationFrame(() => apply(remaining - 1));
      return;
    }
    (target || source).focus();
  };
  apply(5);
  source
    .closest("label")
    ?.scrollIntoView({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
}

export function installPortalControls() {
  let header: Element | null = null;
  const headerSize = new ResizeObserver(entries => {
    const height = entries[0]?.target.getBoundingClientRect().height || 0;
    document.documentElement.style.setProperty("--portal-header-height", `${Math.ceil(height)}px`);
  });
  const enhance = () => {
    const nextHeader = document.querySelector(".command-bar");
    if (nextHeader !== header) {
      headerSize.disconnect();
      header = nextHeader;
      if (header) headerSize.observe(header);
    }
    for (const [source, entry] of mounted)
      if (!source.isConnected) {
        entry.observer.disconnect();
        entry.root.unmount();
        entry.host.remove();
        mounted.delete(source);
      }
    document
      .querySelectorAll<HTMLSelectElement | HTMLInputElement>(
        'select:not([multiple]),input[type="checkbox"],input[type="date"]',
      )
      .forEach((source) => {
        if (
          mounted.has(source) ||
          source.closest("[data-portal-control]") ||
          source.closest(".leaflet-control") ||
          source.dataset.ui === "native"
        )
          return;
        source.id ||= "portal-control-" + ++nextId;
        source.dataset.uiLabel = labelFor(source);
        const host = document.createElement("span");
        host.dataset.portalControl = source.type;
        host.className = "ui-control-host";
        const isDate =
          source instanceof HTMLInputElement && source.type === "date";
        if (isDate) {
          const wrap = document.createElement("span");
          wrap.className = "ui-date-wrap";
          source.before(wrap);
          wrap.append(source, host);
          source.classList.add("ui-date-calendar");
          source.tabIndex = -1;
          source.setAttribute("aria-label", "Календарь: " + labelFor(source));
        } else {
          source.after(host);
          source.hidden = true;
        }
        source.dataset.uiOriginal = "";
        source.labels?.forEach((label) => {
          if (source.dataset.ui !== "segments")
            label.htmlFor = source.id + "-ui";
        });
        const root = createRoot(host);
        const render = () =>
          root.render(
            source instanceof HTMLSelectElement ? (
              <Choice source={source} />
            ) : isDate ? (
              <DateEntry source={source} />
            ) : (
              <CheckControl source={source} />
            ),
          );
        const observer = new MutationObserver(render);
        observer.observe(source, {
          attributes: true,
          attributeFilter: ["disabled", "aria-invalid", "aria-describedby", "checked"],
          childList: true,
          subtree: true,
        });
        mounted.set(source, { root, host, observer });
        render();
      });
  };
  new MutationObserver(enhance).observe(document.body, {
    childList: true,
    subtree: true,
  });
  enhance();
}
